import { clsx } from 'clsx'
import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react'
import styles from './Table.module.css'

type TableProps = HTMLAttributes<HTMLTableElement> & {
  /** Names the table for screen readers, e.g. "Classificação". */
  label: string
  children: ReactNode
}

/**
 * A real `<table>` — standings and fixture lists are tabular data, and a grid
 * of divs loses row/column semantics for anyone using a screen reader.
 * Always wrap wide content: the shell scrolls, the page never does.
 */
export function Table({ label, className, children, ...rest }: TableProps) {
  return (
    <div className={styles.wrap}>
      <table {...rest} aria-label={label} className={clsx(styles.table, className)}>
        {children}
      </table>
    </div>
  )
}

type CellProps = {
  /** Monospaced, tabular, right-aligned — for every number. */
  isNumeric?: boolean
  isMuted?: boolean
}

function TableHead({
  isNumeric = false,
  className,
  children,
  ...rest
}: CellProps & ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th {...rest} scope="col" className={clsx(styles.th, isNumeric && styles.isNumeric, className)}>
      {children}
    </th>
  )
}

function TableCell({
  isNumeric = false,
  isMuted = false,
  className,
  children,
  ...rest
}: CellProps & TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      {...rest}
      className={clsx(styles.td, isNumeric && styles.isNumeric, isMuted && styles.isMuted, className)}
    >
      {children}
    </td>
  )
}

type TableRowProps = HTMLAttributes<HTMLTableRowElement> & {
  /** Marks the viewer's own row with a left rule. */
  isSelf?: boolean
}

function TableRow({ isSelf = false, className, children, ...rest }: TableRowProps) {
  return (
    <tr {...rest} className={clsx(isSelf && styles.isSelf, className)}>
      {children}
    </tr>
  )
}

Table.Head = TableHead
Table.Row = TableRow
Table.Cell = TableCell
